import React, { useEffect, useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import NoData from "../../utils/DataNotFound";
import { ToastContainer } from "react-toastify";
import profile from "../../assets/img/profile.jpg";
import {
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiUser,
} from "react-icons/fi"; // Icons

export default function AllUsers() {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 10;

  const [filters, setFilters] = useState({
    keyword: "",
    fromDate: "",
    toDate: "",
    status: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  const U = (v) => v ?? "-";

  useEffect(() => {
    setPageNo(1);
  }, [filters.keyword, filters.status, filters.sortBy, filters.sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, [pageNo, filters]);

  const fetchUsers = async () => {
    try {
      const response = await makeRequestAuth("post", "/admin/getUserList", {
        keyWord: filters.keyword,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        status: filters.status,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        pageNo,
        size: pageSize,
      });

      setUsers(response.data.list || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.log("Error Fetching Users:", error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await makeRequestAuth(
        "post",
        "/admin/usetStatustoggel",
        {
          id,
        },
      );

      const resData = response?.data;

      if (resData?.error) {
        showErrorToast(resData?.error || "Something went wrong");
        return;
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === id ? { ...user, isDisable: !user.isDisable } : user,
        ),
      );
      showSuccessToast("User status updated successfully!");
    } catch (error) {
      console.error("Error updating user status:", error);
      showErrorToast("Failed to update user status.");
    }
  };

  return (
    <div className="p-4">
      <ToastContainer />

      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            Total Users:{" "}
            <span className="font-semibold text-[#FF6725]">{totalUsers}</span>
          </p>
        </div>
      </div>

      {/* FILTER & TOOLBAR CARD */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Search Bar */}
          <div className="md:col-span-4 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name, email..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6725] focus:ring-1 focus:ring-[#FF6725] transition-all text-sm"
              value={filters.keyword}
              onChange={(e) =>
                setFilters({ ...filters, keyword: e.target.value })
              }
            />
          </div>

          {/* Status Filter */}
          <div className="md:col-span-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiFilter className="text-gray-400" />
              </div>
              <select
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6725] text-sm appearance-none cursor-pointer"
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <option value="">All Status</option>
                <option value="false">Active</option>
                <option value="true">Inactive</option>
              </select>
            </div>
          </div>

          {/* Sorting Group */}
          <div className="md:col-span-5 flex flex-col md:flex-row gap-3">
            <select
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6725] text-sm cursor-pointer"
              value={filters.sortBy}
              onChange={(e) =>
                setFilters({ ...filters, sortBy: e.target.value })
              }
            >
              <option value="createdAt">Date Created</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
            </select>

            <select
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6725] text-sm cursor-pointer"
              value={filters.sortOrder}
              onChange={(e) =>
                setFilters({ ...filters, sortOrder: e.target.value })
              }
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* TABLE SECTION */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-200">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  User Profile
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Current Status
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-10">
                    <NoData title="No Users Found!" />
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr
                    key={user._id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    {/* SR No */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(pageNo - 1) * pageSize + index + 1}
                    </td>

                    {/* Profile */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover border border-gray-200"
                            src={user.image || profile}
                            alt=""
                            onError={(e) => {
                              e.target.src = profile;
                            }} // Fallback
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {U(user.name)}
                          </div>
                          <div className="text-xs text-gray-400">
                            ID: {user._id.slice(-6)}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {U(user.email)}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        {U(user.phone)}
                      </div>
                    </td>

                    {/* Status Badge */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                          user.isDisable === false
                            ? "bg-green-100 text-green-700 border border-green-200"
                            : "bg-red-100 text-red-700 border border-red-200"
                        }`}
                      >
                        {user.isDisable === false ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Action Dropdown */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="relative inline-block w-32">
                        <select
                          value={String(user.isDisable)}
                          onChange={(e) =>
                            handleStatusChange(user._id, e.target.value)
                          }
                          className={`w-full px-3 py-1.5 rounded-md text-xs font-semibold border shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 cursor-pointer transition-all ${
                            user.isDisable === false
                              ? "bg-white border-green-300 text-green-700 hover:border-green-400 focus:ring-green-500"
                              : "bg-white border-red-300 text-red-700 hover:border-red-400 focus:ring-red-500"
                          }`}
                        >
                          <option value="false">Active</option>
                          <option value="true">Inactive</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION FOOTER */}
        {users.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing Page <span className="font-semibold">{pageNo}</span> of{" "}
              <span className="font-semibold">
                {Math.ceil(totalUsers / pageSize)}
              </span>
            </span>

            <div className="flex gap-2">
              <button
                disabled={pageNo <= 1}
                onClick={() => setPageNo(pageNo - 1)}
                className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <FiChevronLeft className="mr-1" /> Previous
              </button>

              <button
                disabled={pageNo >= totalUsers / pageSize}
                onClick={() => setPageNo(pageNo + 1)}
                className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
